import React, { useEffect, useState } from 'react';
import { Sparkles, X, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import MenuShareCard from './MenuShareCard';

/**
 * DashboardWelcomeBanner — premium first-run banner shown immediately after
 * subscription activation. Provides:
 *   • welcome confirmation ("You're live")
 *   • QR code + downloadable PNG
 *   • full share row (Text Message, Email, WhatsApp, Native Share, Copy)
 *
 * Auto-shows ONCE per restaurant based on localStorage flag, OR whenever the
 * URL contains ?welcome=1 / ?billing=success. The user can dismiss any time.
 *
 * This REPLACES the old `/onboarding` wizard "menu items" step — restaurants
 * land on the real dark dashboard right away and still see the share/QR right
 * at the top.
 */

const STORAGE_KEY = (rid) => `nofee_dash_welcomed_${rid}`;

const DashboardWelcomeBanner = ({ restaurant, menuUrl }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!restaurant?.id) return;
    const url = new URL(window.location.href);
    const forced =
      url.searchParams.get('welcome') === '1' ||
      url.searchParams.get('billing') === 'success';
    const dismissed = window.localStorage.getItem(STORAGE_KEY(restaurant.id));
    if (forced || !dismissed) {
      setOpen(true);
    }
  }, [restaurant?.id]);

  const dismiss = () => {
    if (restaurant?.id) {
      window.localStorage.setItem(STORAGE_KEY(restaurant.id), '1');
    }
    // Strip welcome/billing params so refresh doesn't re-open
    const url = new URL(window.location.href);
    url.searchParams.delete('welcome');
    url.searchParams.delete('billing');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : ''));
    setOpen(false);
  };

  const downloadQR = () => {
    const canvas = document.querySelector('[data-testid="welcome-qr-canvas"]');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${restaurant?.slug || 'menu'}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!open || !restaurant || !menuUrl) return null;
  // QR code + share tools are meaningless before the restaurant is live —
  // hide the welcome banner entirely until the owner publishes their
  // restaurant (signalled by `restaurant.go_live_at`).
  if (!restaurant.go_live_at) return null;

  return (
    <div
      className="relative rounded-3xl border border-gold-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-5 sm:p-7 mb-6 shadow-[0_20px_60px_-25px_rgba(212,175,55,0.5)]"
      data-testid="dashboard-welcome-banner"
    >
      {/* Dismiss */}
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Dismiss welcome"
        data-testid="dashboard-welcome-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-gold-300" />
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold-300">
          Your restaurant is live
        </span>
      </div>
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
        Welcome to NoFee, {restaurant?.name?.split(' ')[0] || 'Chef'}.
      </h2>
      <p className="mt-1.5 text-sm text-white/60 max-w-2xl">
        Print the QR for your tables, share your link, and start receiving orders today. Add categories
        and menu items below — your dashboard is your full control panel.
      </p>

      {/* Body — QR + Share */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5 lg:gap-6 items-start">
        {/* QR */}
        <div className="flex sm:flex-row lg:flex-col items-center gap-4 lg:gap-3">
          <div className="bg-white p-2.5 rounded-2xl border-4 border-gold-500/30">
            <QRCodeCanvas
              value={menuUrl}
              size={160}
              level="H"
              includeMargin={false}
              fgColor="#0a0a0a"
              bgColor="#ffffff"
              data-testid="welcome-qr-canvas"
            />
          </div>
          <button
            onClick={downloadQR}
            data-testid="dashboard-welcome-qr-download"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold bg-white/[0.06] hover:bg-white/[0.10] border border-white/15 hover:border-gold-400/40 text-white rounded-xl transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Download QR
          </button>
        </div>

        {/* Share buttons */}
        <div className="min-w-0">
          <MenuShareCard
            restaurantName={restaurant?.name || 'our restaurant'}
            menuUrl={menuUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardWelcomeBanner;
